// ─────────────────────────────────────────────────────────────────────────────
//  DomainRenewalPrompt
//
//  The recurring renewal prompt. Mounted once in App.js so it can appear over
//  any screen after login.
//
//  Behaviour, and the reasoning behind it:
//
//   • The backend decides the stage (notice → warning → urgent → expired) from
//     days remaining. This component only decides who sees it and how often.
//
//   • Audience: administrators carry it alone until 14 days out, so ward staff
//     aren't interrupted over billing they can't act on. Inside 14 days the
//     backend flips `showToAll` and everyone sees it — that is what actually
//     gets an invoice paid.
//
//   • It cannot be dismissed by clicking away or pressing Escape. The only way
//     out is "I have reviewed this invoice", which records WHO acknowledged it
//     against the current billing period.
//
//   • Acknowledging silences it for a while, not forever, and the window
//     shortens as the deadline closes (3 days → 1 day → 6 hours → every login).
//     So it keeps coming back until the invoice is actually settled, without
//     trapping a nurse mid-shift behind a modal she cannot resolve.
//
//   • The only thing that stops it permanently is a real renewal. The snooze
//     key and the server-side acknowledgement are both scoped to the current
//     expires_at, so moving that date forward invalidates both at once and the
//     prompt goes quiet on its own. Nothing to reset by hand.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Button, Typography,
  CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import {
  Print as PrintIcon,
  CheckCircleOutline as AckIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

import { domainRenewalStatus, acknowledgeDomainNotice } from '../BaseURL';
import DomainRenewalInvoice, { printDomainInvoice, STAGE_META } from './DomainRenewalInvoice';

/* ═══ TURNED OFF ═══════════════════════════════════════════════════════════
 *
 *  This prompt is disabled. Flip ENABLED back to true to restore it.
 *
 *  WHY IT WAS TURNED OFF
 *
 *  It appeared over the dashboard on every login reading "Domain registration
 *  has lapsed — 9138 days overdue", due by 17 August 2001, with no amount set.
 *
 *  The licence was never wrong. expires_at is 17 August 2027, with 358 days to
 *  run. The backend's asDateOnly() did `String(value).slice(0, 10)` on a value
 *  that arrives as a JS Date, not a string — so "2027-08-17" became the first
 *  ten characters of "Tue Aug 17 2027…", which is "Tue Aug 17", which re-parses
 *  to the year 2001 because it carries no year at all. Hence 2001, hence 9,138
 *  days, hence stage 'expired'. And SNOOZE_HOURS.expired is 0, so "I have
 *  reviewed this invoice" bought no quiet period and the modal returned on the
 *  very next login. Unclosable, un-snoozable, and entirely manufactured.
 *
 *  THAT IS FIXED at source in asDateOnly, and the endpoint now reports stage
 *  'ok'. So this prompt would already stay silent with ENABLED = true.
 *
 *  It is still off, for the reason below.
 *
 *  BEFORE TURNING IT BACK ON: MOVE IT OUT OF App.js
 *
 *  Mounted there it can appear over ANY screen, which is what made it a
 *  dashboard interruption rather than a billing notice. It belongs inside the
 *  Admin Panel, where the person who can actually settle it is already standing.
 *  Left where it is, it will do the same thing again when the 60-day window opens
 *  — in about 298 days — and be just as unwelcome for being correct that time.
 *
 *  The backend's ladder already matches the two-to-three-month intent
 *  (DOMAIN_STAGE_NOTICE is 60 days, admins only until 14), so what needs changing
 *  is where this MOUNTS, not when it fires.
 * ═════════════════════════════════════════════════════════════════════════ */
const ENABLED = false;

/* Roles that are accountable for the facility's billing. */
const ADMIN_ROLES = ['System Administrator', 'Head Of Assembly'];

/* How long an acknowledgement buys, per stage. Tightens as the date closes;
   once expired there is no quiet period at all. */
const SNOOZE_HOURS = { notice: 72, warning: 24, urgent: 6, expired: 0 };

/* Re-poll while the app is left open overnight, so a shift that starts at the
   30-day mark sees the escalation without needing a page refresh. */
const POLL_INTERVAL_MS = 60 * 60 * 1000;   // 1 hour

const snoozeKey = (expiresAt) => `domainNoticeSnooze_${expiresAt || 'unknown'}`;

const isSnoozed = (expiresAt) => {
  try {
    const until = Number(localStorage.getItem(snoozeKey(expiresAt)) || 0);
    return Date.now() < until;
  } catch { return false; }
};

const setSnooze = (expiresAt, hours) => {
  try {
    if (!hours) return;                       // expired → never snooze
    localStorage.setItem(snoozeKey(expiresAt), String(Date.now() + hours * 3600 * 1000));
  } catch { /* private mode — prompt just shows again, which is the safe default */ }
};

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || 'null'); }
  catch { return null; }
};

const DomainRenewalPrompt = () => {
  const [status, setStatus]   = useState(null);
  const [open, setOpen]       = useState(false);
  const [saving, setSaving]   = useState(false);

  // Route changes are the signal that someone just logged in. Without this the
  // prompt would wait for the next hourly poll, so the administrator could get
  // most of a shift in before seeing it. '/' is the login screen — a stale
  // userInfo in localStorage must not put a modal over the login form.
  const location = useLocation();
  const onLoginScreen = location.pathname === '/';

  const evaluate = useCallback((data) => {
    // Nothing configured, licence server down, or plenty of time left.
    if (!data || !data.configured) return;
    if (!data.stage || data.stage === 'ok') return;

    const user = readUser();
    if (!user) return;                                   // not logged in

    // Audience gate — admins only until the backend opens it to everyone.
    const isAdmin = ADMIN_ROLES.includes(user.role);
    if (!data.showToAll && !isAdmin) return;

    if (isSnoozed(data.invoice?.expiresAt)) return;

    setOpen(true);
  }, []);

  const load = useCallback(async () => {
    // Disabled: no polling either. Leaving the hourly request running while the
    // dialog can never open would be a call every user makes for nothing.
    if (!ENABLED) return;
    // Cheap guards: never poll from the login screen or before login.
    if (onLoginScreen || !readUser()) return;
    try {
      const res = await axios.get(domainRenewalStatus);
      setStatus(res.data);
      evaluate(res.data);
    } catch (err) {
      // Silent by design. A renewal notice is not worth an error popup, and a
      // transient network blip must never lock the UI behind this modal.
      console.warn('[domain-renewal] status check failed:', err.message);
    }
  }, [evaluate, onLoginScreen]);

  useEffect(() => {
    if (onLoginScreen) { setOpen(false); return; }
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load, onLoginScreen]);

  const handleAcknowledge = async () => {
    const user = readUser();
    setSaving(true);
    try {
      await axios.post(acknowledgeDomainNotice, {
        acknowledgedBy: user?.username || user?.name || 'unknown',
      });
      setSnooze(status?.invoice?.expiresAt, SNOOZE_HOURS[status?.stage] ?? 24);
      setOpen(false);
    } catch (err) {
      // Failing to record the acknowledgement must not strand the user. Snooze
      // locally so they can keep working; the server will simply ask again.
      console.warn('[domain-renewal] acknowledge failed:', err.message);
      setSnooze(status?.invoice?.expiresAt, 1);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const ok = printDomainInvoice(status);
    if (ok === false) {
      Swal.fire({
        icon: 'info',
        title: 'Pop-up blocked',
        text: 'Allow pop-ups for this site to print or save the invoice as PDF.',
        confirmButtonColor: '#11998e',
      });
    }
  };

  //  Rendered as nothing while disabled. The component stays mounted in App.js
  //  and every hook above stays in place, so ENABLED = true is genuinely the only
  //  edit needed to bring it back — and no hook order changes, which is what
  //  makes an early return like this safe here.
  if (!ENABLED) return null;

  if (onLoginScreen || !status || !status.invoice) return null;

  const meta     = STAGE_META[status.stage] || STAGE_META.ok;
  const expired  = status.stage === 'expired';
  const user     = readUser();
  const isAdmin  = ADMIN_ROLES.includes(user?.role);

  return (
    <Dialog
      open={open}
      // No onClose handler: clicking away and Escape both do nothing. The
      // acknowledge button is the only exit.
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{
        px: 3, py: 2,
        background: `linear-gradient(135deg, ${meta.accent}, ${meta.accent}dd)`,
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>
        <Box>
          <Typography sx={{ fontSize: '1.02rem', fontWeight: 700, lineHeight: 1.2 }}>
            {expired ? 'Domain registration has lapsed' : 'Domain renewal invoice'}
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', opacity: 0.9 }}>
            {status.invoice.domainName || status.invoice.hospital} · {meta.label}
          </Typography>
        </Box>
        <Tooltip title="Print or save as PDF">
          <IconButton onClick={handlePrint} sx={{ color: '#fff' }}>
            <PrintIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, background: '#f8fafc' }}>
        <DomainRenewalInvoice data={status} compact />

        {!isAdmin && (
          <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: 2, textAlign: 'center' }}>
            Please inform your facility administrator so the renewal can be settled.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', gap: 1 }}>
        <Button
          onClick={handlePrint}
          startIcon={<PrintIcon />}
          variant="outlined"
          sx={{ textTransform: 'none', color: '#475569', borderColor: '#cbd5e1' }}
        >
          Print / Save PDF
        </Button>
        <Button
          onClick={handleAcknowledge}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AckIcon />}
          sx={{
            textTransform: 'none', fontWeight: 700, px: 2.5,
            background: meta.accent,
            '&:hover': { background: meta.accent, filter: 'brightness(0.92)' },
          }}
        >
          {saving ? 'Recording…' : 'I have reviewed this invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DomainRenewalPrompt;
